import { useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { AppLayout } from '@/components/layout/AppLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { 
  PiggyBank, Brain, Target, TrendingUp, Shield, 
  Send, Loader2, Bot, User, Sparkles 
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const features = [
  { icon: Brain, title: 'AI Portfolio Management', description: 'Smart algorithms optimize your investments' },
  { icon: Target, title: 'Goal-Based Investing', description: 'Set targets and track progress' },
  { icon: Shield, title: 'Risk Assessment', description: 'Personalized risk profiling' },
  { icon: TrendingUp, title: 'Performance Tracking', description: 'Real-time portfolio analytics' },
];

export default function WealthPage() {
  const { token } = useAuth();
  const [query, setQuery] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  const sendMessage = async () => {
    if (!query.trim()) return;

    const userMessage = { role: 'user', content: query };
    setChatHistory(prev => [...prev, userMessage]);
    setQuery('');
    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/ai/chat`, {
        query: query,
        agent_type: 'catalog',
        context: { vertical: 'wealth' }
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const aiMessage = { role: 'assistant', content: response.data.response };
      setChatHistory(prev => [...prev, aiMessage]);
    } catch (error) {
      toast.error('Failed to get response from AI');
      setChatHistory(prev => [...prev, { 
        role: 'assistant', 
        content: 'I apologize, but I\'m having trouble processing your request. Please try again.' 
      }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AppLayout title="Jambo Wealth">
      <div className="p-4 lg:p-6 space-y-6">
        {/* Hero */}
        <Card className="bg-gradient-to-br from-amber-500/10 to-orange-500/5 border-0">
          <CardContent className="p-6 lg:p-10">
            <div className="flex items-start gap-4">
              <div className="w-16 h-16 rounded-2xl bg-amber-500 flex items-center justify-center">
                <PiggyBank className="h-8 w-8 text-white" />
              </div>
              <div>
                <h2 className="text-2xl lg:text-3xl font-bold mb-2">AI-Powered Wealth Management</h2>
                <p className="text-muted-foreground max-w-2xl">
                  Let our intelligent algorithms help you build, protect, and grow your wealth with personalized strategies tailored to your goals.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Features */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {features.map((feature) => (
            <Card key={feature.title} className="card-hover" data-testid={`feature-${feature.title.toLowerCase().replace(/\s+/g, '-')}`}>
              <CardContent className="p-5">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                  <feature.icon className="h-6 w-6 text-primary" />
                </div>
                <h3 className="font-semibold mb-1">{feature.title}</h3>
                <p className="text-sm text-muted-foreground">{feature.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* AI Chat */}
        <Card data-testid="ai-wealth-chat">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-amber-500" />
              Wealth AI Assistant
            </CardTitle>
            <CardDescription>
              Ask me anything about investments, portfolio strategies, or financial planning
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Chat Messages */}
            <div className="h-80 overflow-y-auto mb-4 p-4 bg-muted/30 rounded-xl space-y-4">
              {chatHistory.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center text-muted-foreground">
                  <Bot className="h-12 w-12 mb-4 opacity-50" />
                  <p className="font-medium">Start a conversation</p>
                  <p className="text-sm">Ask about investment strategies, risk assessment, or portfolio optimization</p>
                </div>
              ) : (
                chatHistory.map((msg, index) => (
                  <div 
                    key={index} 
                    className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    {msg.role === 'assistant' && (
                      <div className="w-8 h-8 rounded-full bg-amber-500/10 flex items-center justify-center flex-shrink-0">
                        <Bot className="h-4 w-4 text-amber-500" />
                      </div>
                    )}
                    <div className={`chat-bubble p-3 ${
                      msg.role === 'user' ? 'chat-bubble-user' : 'chat-bubble-ai'
                    }`}>
                      <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                    </div>
                    {msg.role === 'user' && (
                      <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                        <User className="h-4 w-4 text-white" />
                      </div>
                    )}
                  </div>
                ))
              )}
              {loading && (
                <div className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-amber-500/10 flex items-center justify-center">
                    <Bot className="h-4 w-4 text-amber-500" />
                  </div>
                  <div className="chat-bubble chat-bubble-ai p-3">
                    <Loader2 className="h-4 w-4 animate-spin" />
                  </div>
                </div>
              )}
            </div>

            {/* Input */}
            <div className="flex gap-2">
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Ask about investment strategies..."
                onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                disabled={loading}
                data-testid="wealth-ai-input"
              />
              <Button 
                onClick={sendMessage} 
                disabled={loading || !query.trim()}
                className="rounded-full px-6"
                data-testid="wealth-ai-send"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <div className="grid md:grid-cols-3 gap-4">
          <Card className="card-hover">
            <CardContent className="p-5">
              <h3 className="font-semibold mb-2">Start Investing</h3>
              <p className="text-sm text-muted-foreground mb-4">Begin your wealth journey with as little as KES 1,000</p>
              <Button className="w-full rounded-full" data-testid="start-investing-btn">Get Started</Button>
            </CardContent>
          </Card>
          <Card className="card-hover">
            <CardContent className="p-5">
              <h3 className="font-semibold mb-2">Risk Assessment</h3>
              <p className="text-sm text-muted-foreground mb-4">Take our quiz to determine your risk profile</p>
              <Button variant="outline" className="w-full rounded-full" data-testid="risk-assessment-btn">Take Quiz</Button>
            </CardContent>
          </Card>
          <Card className="card-hover">
            <CardContent className="p-5">
              <h3 className="font-semibold mb-2">Talk to Advisor</h3>
              <p className="text-sm text-muted-foreground mb-4">Schedule a call with our wealth experts</p>
              <Button variant="outline" className="w-full rounded-full" data-testid="talk-advisor-btn">Book Call</Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </AppLayout>
  );
}
